// src/pages/Register.js
//
// The single signup form. Runs in one of two modes:
//
//   CREATE   — nobody signed in. Full form, creates the account.
//   COMPLETE — signed in but the profile is missing state, grade, or role.
//              Same form minus email and password, updates in place.
//
// COMPLETE mode exists because of a trap this page used to create. The navbar
// modal had its own signup form that wrote no `intendedRole`, so after
// verifying their email people landed on ChoosePlan's "We're missing a
// detail" screen, which sent them here — to a create-account form, for an
// account that already existed. The only thing waiting for them was "An
// account with that email already exists." No way forward.
//
// That duplicate form is gone (the modal's Register tab links here now), but
// COMPLETE mode still matters: every account created before this change has
// the same gap, as does anyone an admin creates by hand.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  updateProfile,
  onAuthStateChanged,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase/firebaseConfig";
import { sendVerificationEmail } from "../utils/sendVerificationEmail";
import { US_STATES, GRADES } from "../config/geography";
import { SIGNUP_ROLES, UNPAID_ROLE, composeGrantedRole } from "../config/plans";
import {
  lookupEducatorByCode,
  joinEducatorRoster,
  ensureEducatorCode,
} from "../utils/educatorCode";
import { TITLE_OPTIONS } from "../utils/displayName";
import { SUBJECTS } from "../data/subjects";
import { gradeBand, BAND } from "../config/plans";
import {
  PAID_SUBJECTS,
  FREE_SUBJECT,
  optionsForSubject,
  setCourse,
} from "../utils/enrollment";
import TermsAcceptance from "../components/legal/TermsAcceptance";
import { TERMS_VERSION } from "../config/terms";
import { isEmailConfirmed } from "../utils/studentLogin";

const MODE = { LOADING: "loading", CREATE: "create", COMPLETE: "complete" };

// Everyone gets a real look at the product before deciding. After this runs
// out they drop to ACCESS.FREE, which is FREE_TOPIC_LIMIT topics per subject
// (see src/hooks/useAccessLevel.js) rather than nothing at all.
//
// The free trial: 60 days of everything except Request Custom Material (and
// a class of up to 5 students for teachers). To test the end of a trial, set
// an account's trial.endDate to yesterday in Admin ▸ Users instead of
// changing this. The Firestore rules refuse a trial longer than 61 days.
const TRIAL_DAYS = 60;

const ROLE_OPTIONS = [
  { key: SIGNUP_ROLES.EDUCATOR, label: "Educator", hint: "Teacher or school staff" },
  { key: SIGNUP_ROLES.PARENT, label: "Parent", hint: "For my own child" },
  { key: SIGNUP_ROLES.STUDENT, label: "Student seats", hint: "Buying seats for students" },
  { key: SIGNUP_ROLES.DISTRICT, label: "School / District", hint: "Purchase order" },
];

export default function Register() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [mode, setMode] = useState(MODE.LOADING);
  const [form, setForm] = useState({
    sirName: "",
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirm: "",
    state: "",
    grade: "",
    role: "",
    educatorCode: "",
    // Unchecked by default and never pre-ticked — a pre-ticked box is not an
    // affirmative act and is the fastest way to make the whole agreement
    // unenforceable. See components/legal/TermsAcceptance.js.
    acceptedTerms: false,
  });

  // Educator lookup: null = nothing entered, "searching", an object once
  // found, or "notfound". Held separately from `form` because it's the
  // result of a lookup, not something the person typed.
  // The student's schedule: { math: "geometry", science: "biology-1", ... }
  // Held apart from `form` because it isn't a field they type — it's built
  // one subject at a time, and its shape (see utils/enrollment.js) is what
  // enforces one course per subject.
  const [courses, setCourses] = useState({});

  const [educatorMatch, setEducatorMatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Decide which mode we're in, and prefill whatever already exists.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setMode(MODE.CREATE);
        return;
      }

      const snap = await getDoc(doc(db, "users", user.uid));
      const profile = snap.exists() ? snap.data() : {};
      const complete = profile.state && profile.grade && profile.intendedRole;

      if (complete) {
        navigate(isEmailConfirmed(user) ? "/dashboard" : "/verify-email", {
          replace: true,
        });
        return;
      }

      // Carry over whatever the old form managed to save, so this is
      // gap-filling rather than retyping everything.
      setForm((f) => ({
        ...f,
        sirName: profile.sirName || f.sirName,
        firstName: profile.firstName || profile.name?.split(" ")[0] || "",
        lastName:
          profile.lastName || profile.name?.split(" ").slice(1).join(" ") || "",
        email: profile.email || user.email || "",
        state: profile.state || f.state,
        grade: profile.grade || f.grade,
        // Older accounts stored a bare "educator"/"parent" in `role`.
        role:
          profile.intendedRole ||
          (Object.values(SIGNUP_ROLES).includes(profile.role)
            ? profile.role
            : f.role),
      }));
      setMode(MODE.COMPLETE);
    });
    return () => unsub();
  }, [navigate]);

  // Prefill from the marketing picker or a seat invite.
  useEffect(() => {
    const state = (params.get("state") || "").toUpperCase();
    const grade = params.get("grade") || "";
    const role = params.get("role") || "";
    const email = params.get("email") || "";
    setForm((f) => ({
      ...f,
      state: US_STATES.includes(state) ? state : f.state,
      grade: GRADES.includes(grade) ? grade : f.grade,
      role: Object.values(SIGNUP_ROLES).includes(role) ? role : f.role,
      email: email || f.email,
    }));
  }, [params]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Linking to an educator is confirmed by name before it's saved. A code is
  // a string of digits — nobody can tell a typo from a correct entry by
  // looking at it, and a wrong link is invisible until a teacher wonders why
  // a child never appears in their class list.
  const checkEducatorCode = async () => {
    const code = form.educatorCode.trim();
    if (!code) {
      setEducatorMatch(null);
      return;
    }

    setEducatorMatch("searching");

    try {
      const found = await lookupEducatorByCode(code);
      setEducatorMatch(found || "notfound");
    } catch (err) {
      console.warn("Educator lookup failed:", err?.message || err);
      setEducatorMatch("notfound");
    }
  };

  // Only parents and students follow an educator. An educator gets their own
  // code assigned; entering someone else's would put them in that person's
  // classroom.
  const showEducatorCodeField =
    form.role === SIGNUP_ROLES.PARENT || form.role === SIGNUP_ROLES.STUDENT;

  // Grades 6-12 pick their courses here, before the account exists.
  //
  // WHY AT REGISTRATION AND NOT AFTER PAYMENT
  // -----------------------------------------
  // The schedule decides which subject buttons the portal shows, and the
  // portal is free to browse — five topics per subject with no card. Someone
  // who signs up free and lands on a nav of four generic subjects, three of
  // which they aren't taking, has no way to find their actual course. The
  // schedule has to exist before the first page load, which means it has to
  // be collected here.
  //
  // It is NOT a purchase. Nothing on this screen costs anything; it's the
  // timetable, and the plan page later prices whatever's on it.
  const isSecondary = gradeBand(form.grade) === BAND.SECONDARY;

  const subjectChoices = useMemo(() => {
    if (!isSecondary || !form.state || !form.grade) return [];

    return SUBJECTS.map((subject) => ({
      subject,
      // "other" is included with every plan and with none, so it's resolved
      // silently rather than offered — one fewer decision on a form that
      // already asks for six things.
      paid: PAID_SUBJECTS.includes(subject.value),
      options: optionsForSubject({
        state: form.state,
        grade: form.grade,
        subject: subject.value,
      }),
    })).filter((entry) => entry.paid);
  }, [isSecondary, form.state, form.grade]);

  // "Other" — art, music, health, PE. No course split anywhere, so it
  // resolves to the grade token and is written without ever being asked
  // about.
  const otherTrack = useMemo(() => {
    if (!isSecondary || !form.state || !form.grade) return "";
    return (
      optionsForSubject({
        state: form.state,
        grade: form.grade,
        subject: FREE_SUBJECT,
      })[0]?.token || ""
    );
  }, [isSecondary, form.state, form.grade]);

  // Changing state or grade invalidates every choice made under the old one —
  // "geometry" is meaningless once the grade is 3rd, and carrying it forward
  // would write a schedule referring to courses this student can't take.
  useEffect(() => {
    setCourses({});
  }, [form.state, form.grade]);

  const previewRole = useMemo(
    () => composeGrantedRole({ state: form.state, grade: form.grade, role: form.role }),
    [form.state, form.grade, form.role]
  );

  const validate = () => {
    if (!form.firstName.trim()) return "Please enter your first name.";
    if (!form.lastName.trim()) return "Please enter your last name.";
    if (mode === MODE.CREATE) {
      if (!form.email.trim()) return "Please enter your email.";
      if (form.password.length < 8) return "Password must be at least 8 characters.";
      if (form.password !== form.confirm) return "Passwords don't match.";
    }
    if (!form.state) return "Please select your state.";
    if (!form.grade) return "Please select a grade.";
    if (!form.role) return "Please tell us who's signing up.";
    // Last check, so someone who has filled everything else in isn't stopped
    // by the checkbox before they've seen the rest of the form.
    if (!form.acceptedTerms) {
      return "Please confirm you've read the Terms & Conditions.";
    }
    return null;
  };

  const handleSubmit = async () => {
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError("");

    const fullName = `${form.firstName.trim()} ${form.lastName.trim()}`.trim();

    try {
      if (mode === MODE.COMPLETE) {
        const user = auth.currentUser;
        if (!user) {
          setMode(MODE.CREATE);
          setBusy(false);
          return;
        }

        // `role` is deliberately absent from this update. Firestore rules
        // block a user from changing their own role after creation, so
        // including it would get the whole write rejected — even though
        // every other field in it is permitted.
        await setDoc(
          doc(db, "users", user.uid),
          {
            sirName: form.sirName,
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            name: fullName,
            state: form.state,
            grade: form.grade,
            intendedRole: form.role,
            profileCompletedAt: serverTimestamp(),
          },
          { merge: true }
        );

        // Allocated in the browser via a counter transaction, NOT via the
        // ensureEducatorCode callable that used to be called here. Two
        // reasons: the callable's preflight was being rejected, and — more
        // importantly — it issues a random six-character code (K7M2QP) while
        // utils/educatorCode.js issues sequential numbers. Whichever ran
        // first decided the format, so the same product was handing out two
        // incompatible kinds of code.
        try {
          await ensureEducatorCode(user.uid, {
            ...form,
            intendedRole: form.role,
          });
        } catch (codeErr) {
          console.warn("Educator code not assigned yet:", codeErr);
        }

        navigate(isEmailConfirmed(user) ? "/dashboard" : "/verify-email", {
          replace: true,
        });
        return;
      }

      const cred = await createUserWithEmailAndPassword(
        auth,
        form.email.trim(),
        form.password
      );
      await updateProfile(cred.user, { displayName: fullName });

      const trialEnd = new Date();
      trialEnd.setDate(trialEnd.getDate() + TRIAL_DAYS);

      // `role` is UNPAID_ROLE, not the real one. Rules let a user set this
      // field once at creation and never again, so this write must not grant
      // anything — PrivateRoute matches with String.includes(), and a value
      // containing "educator" would clear an educator gate for free. The
      // Stripe webhook promotes them after payment.
      await setDoc(doc(db, "users", cred.user.uid), {
        sirName: form.sirName,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        name: fullName,
        email: form.email.trim().toLowerCase(),
        role: UNPAID_ROLE,
        intendedRole: form.role,
        state: form.state,
        grade: form.grade,
        // Enrolment, not entitlement — see utils/enrollment.js. Empty for
        // K-5, where every subject is addressed by grade and there is
        // nothing to choose. "other" is filled in here rather than asked
        // about: it's free with every plan, so a question about it is a
        // question with only one useful answer.
        ...(isSecondary
          ? {
              courses: {
                ...courses,
                ...(otherTrack ? { [FREE_SUBJECT]: otherTrack } : {}),
              },
            }
          : {}),
        // Only a code the person actually confirmed by name is saved. A raw
        // typed value could be a typo that silently links a child to a
        // stranger's classroom.
        ...(educatorMatch && typeof educatorMatch === "object"
          ? {
              educatorCode: educatorMatch.code,
              followsEducatorUid: educatorMatch.uid,
            }
          : {}),
        trial: {
          status: "active",
          endDate: trialEnd,
          updatedAt: serverTimestamp(),
        },
        // The acceptance RECORD, not just the gate. A checkbox that blocks a
        // button but writes nothing proves nothing later — this is what makes
        // "they agreed to the non-refundable clause" a checkable fact, tied to
        // the exact revision they saw.
        termsAcceptance: {
          version: TERMS_VERSION,
          acceptedAt: serverTimestamp(),
          context: "signup",
        },
        avatarUrl: "",
        joinDate: new Date().toISOString(),
        createdAt: serverTimestamp(),
      });

      // Join the educator's roster so they appear in the class list. Done
      // here rather than in a rule or a trigger because the student writes
      // their own entry — no elevated permission needed on either side.
      if (educatorMatch && typeof educatorMatch === "object") {
        await joinEducatorRoster(cred.user.uid, educatorMatch.code, {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim().toLowerCase(),
          grade: form.grade,
          state: form.state,
          intendedRole: form.role,
        });
      }

      // Educator code. Server-side because uniqueness is a privacy boundary,
      // not a nicety — firestore.rules lets anyone sharing a code read the
      // other's profile. Failure here is non-fatal: the dashboard retries on
      // mount, so a hiccup costs a page load, not the signup.
      // Same as above — sequential code from the counter, not the callable's
      // random string. Non-fatal: DashboardEducator retries on mount, so a
      // hiccup here costs a page load rather than the signup.
      try {
        await ensureEducatorCode(cred.user.uid, {
          sirName: form.sirName,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          name: fullName,
          state: form.state,
          grade: form.grade,
          intendedRole: form.role,
        });
      } catch (codeErr) {
        console.warn("Educator code not assigned yet:", codeErr);
      }

      // The account exists in Auth and in Firestore by this point. Anything
      // that fails from here on is a follow-up step, not the registration —
      // so it gets its own catch. Sharing the outer one is what produced the
      // reported bug: an unauthorized continue URL threw, the outer catch
      // printed "Could not save your details", and the person was left
      // staring at a failure message for an account that had been created
      // successfully. Trying again then hit "email already in use", with no
      // way forward.
      let verificationSent = true;
      try {
        await sendVerificationEmail(cred.user);
      } catch (mailErr) {
        verificationSent = false;
        console.error("Confirmation email not sent:", mailErr);
      }

      // Either way we move on. /verify-email has a Resend button, and it
      // polls, so an account that never got its first email is recoverable
      // from there instead of being stranded on this form.
      navigate("/verify-email", { replace: true, state: { verificationSent } });
    } catch (err) {
      console.error("Registration error:", err);
      const map = {
        "auth/email-already-in-use":
          "An account with that email already exists — sign in and we'll pick up where you left off.",
        "auth/invalid-email": "That email address doesn't look right.",
        "auth/weak-password": "Please choose a stronger password.",
      };
      setError(map[err.code] || "Could not save your details. Please try again.");
      setBusy(false);
    }
  };

  if (mode === MODE.LOADING) {
    return (
      <div style={s.page}>
        <div style={s.card}><p>Loading…</p></div>
      </div>
    );
  }

  const completing = mode === MODE.COMPLETE;

  return (
    <div style={s.page}>
      <div style={s.card}>
        <h1 style={s.title}>
          {completing ? "Finish setting up your account" : "Create your account"}
        </h1>
        <p style={s.sub}>
          {completing
            ? "Your account already exists — we just need your state, grade, and who you're signing up as."
            : "Free to start. Five topics per subject unlock right away."}
        </p>

        {error && <div style={s.error}>{error}</div>}

        <div style={s.row}>
          <div style={{ ...s.col, flex: "0 0 130px" }}>
            <label style={s.label}>
              Title <span style={s.optional}>(optional)</span>
            </label>
            {/* Educators are addressed by title and surname everywhere a
                class is named — "Welcome to Mr. Eric's Math class". Without
                this the greeting has to fall back to a first name, which
                isn't how a classroom works. Optional, because a parent or an
                older student may not want one, and blank simply falls back
                to the first name. */}
            <select
              style={s.input}
              value={form.sirName}
              onChange={set("sirName")}
            >
              <option value="">None</option>
              {TITLE_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div style={s.col}>
            <label style={s.label}>First name</label>
            <input
              style={s.input}
              value={form.firstName}
              onChange={set("firstName")}
              autoComplete="given-name"
            />
          </div>
          <div style={s.col}>
            <label style={s.label}>Last name</label>
            <input
              style={s.input}
              value={form.lastName}
              onChange={set("lastName")}
              autoComplete="family-name"
            />
          </div>
        </div>

        {form.role === SIGNUP_ROLES.EDUCATOR && form.lastName.trim() && (
          <p style={s.namePreview}>
            Your students will see{" "}
            <strong>
              {[form.sirName, form.lastName.trim()].filter(Boolean).join(" ")}
            </strong>
          </p>
        )}

        {!completing && (
          <>
            <label style={s.label}>Email</label>
            <input
              style={s.input}
              type="email"
              value={form.email}
              onChange={set("email")}
              autoComplete="email"
            />

            <div style={s.row}>
              <div style={s.col}>
                <label style={s.label}>Password</label>
                <input
                  style={s.input}
                  type="password"
                  value={form.password}
                  onChange={set("password")}
                  autoComplete="new-password"
                />
                <span style={s.hint}>At least 8 characters</span>
              </div>
              <div style={s.col}>
                <label style={s.label}>Confirm password</label>
                <input
                  style={s.input}
                  type="password"
                  value={form.confirm}
                  onChange={set("confirm")}
                  autoComplete="new-password"
                />
              </div>
            </div>
          </>
        )}

        {completing && form.email && (
          <p style={s.signedInAs}>
            Signed in as <strong>{form.email}</strong>
          </p>
        )}

        <div style={s.row}>
          <div style={s.col}>
            <label style={s.label}>State</label>
            <select style={s.input} value={form.state} onChange={set("state")}>
              <option value="">Select state</option>
              {US_STATES.map((st) => (
                <option key={st} value={st}>{st}</option>
              ))}
            </select>
          </div>
          <div style={s.col}>
            <label style={s.label}>Grade</label>
            <select style={s.input} value={form.grade} onChange={set("grade")}>
              <option value="">Select grade</option>
              {GRADES.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>
        </div>

        {isSecondary && subjectChoices.length > 0 && (
          <div style={s.scheduleBlock}>
            <label style={s.label}>
              Which classes? <span style={s.optional}>(you can add more later)</span>
            </label>
            <p style={s.scheduleHelp}>
              From {form.grade} you change teachers by subject, so tell us
              what's on the timetable. Pick one class per subject — you can't
              be in two maths at once. Art, music and PE are included free.
            </p>

            {subjectChoices.map(({ subject, options }) => (
              <div key={subject.value} style={s.scheduleGroup}>
                <div style={s.scheduleSubject}>
                  <span aria-hidden="true">{subject.icon}</span> {subject.label}
                </div>

                {/* Radios, not checkboxes. The one-per-subject rule is real —
                    a student takes one maths class — and a radio group says
                    so without needing an error message when someone ticks a
                    second box. Selecting Algebra 2 simply deselects Geometry,
                    which is also what setCourse() does to the stored map. */}
                {options.map((opt) => (
                  <label key={opt.token} style={s.scheduleRow}>
                    <input
                      type="radio"
                      name={`course-${subject.value}`}
                      checked={courses[subject.value] === opt.token}
                      onChange={() =>
                        setCourses((c) => setCourse(c, subject.value, opt.token))
                      }
                      style={s.scheduleRadio}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}

                {/* Not taking it at all is a real answer, and a common one —
                    plenty of 12th graders have no science. Without this the
                    only way out of a radio group is to pick something wrong. */}
                <label style={s.scheduleRow}>
                  <input
                    type="radio"
                    name={`course-${subject.value}`}
                    checked={!courses[subject.value]}
                    onChange={() =>
                      setCourses((c) => setCourse(c, subject.value, null))
                    }
                    style={s.scheduleRadio}
                  />
                  <span style={s.scheduleNone}>Not taking {subject.label}</span>
                </label>
              </div>
            ))}
          </div>
        )}

        <label style={s.label}>Who's signing up?</label>
        <div style={s.roleGrid}>
          {ROLE_OPTIONS.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setForm((f) => ({ ...f, role: r.key }))}
              style={{ ...s.roleBtn, ...(form.role === r.key ? s.roleBtnActive : {}) }}
            >
              <strong>{r.label}</strong>
              <span style={s.roleHint}>{r.hint}</span>
            </button>
          ))}
        </div>

        {showEducatorCodeField && (
          <div style={s.eduCodeBlock}>
            <label style={s.label}>
              Educator code <span style={s.optional}>(optional)</span>
            </label>
            <p style={s.eduCodeHelp}>
              Following a teacher? Enter the code they gave you and we'll show
              you their name to check. Leave it blank if you're on your own —
              you can add it later.
            </p>

            <div style={s.eduCodeRow}>
              <input
                style={{ ...s.input, marginBottom: 0 }}
                value={form.educatorCode}
                onChange={(e) => {
                  setForm((f) => ({ ...f, educatorCode: e.target.value }));
                  setEducatorMatch(null);
                }}
                onBlur={checkEducatorCode}
                placeholder="e.g. 471020"
                inputMode="numeric"
                autoComplete="off"
              />
              <button
                type="button"
                style={s.eduCodeCheck}
                onClick={checkEducatorCode}
                disabled={!form.educatorCode.trim()}
              >
                Check
              </button>
            </div>

            {educatorMatch === "searching" && (
              <p style={s.eduCodeSearching}>Looking that up…</p>
            )}

            {educatorMatch === "notfound" && (
              <p style={s.eduCodeBad}>
                No educator found with that code. Double-check it with your
                teacher, or leave it blank and add it later.
              </p>
            )}

            {educatorMatch && typeof educatorMatch === "object" && (
              <div style={s.eduCodeGood}>
                <strong>{educatorMatch.displayName}</strong>
                {educatorMatch.grade ? ` · Grade ${educatorMatch.grade}` : ""}
                {educatorMatch.state
                  ? ` · ${educatorMatch.state.toUpperCase()}`
                  : ""}
                <div style={s.eduCodeConfirm}>
                  If that's your teacher, you're all set — finish signing up
                  below.
                </div>
              </div>
            )}
          </div>
        )}

        <p style={s.minorNote}>
          Anyone under 18 needs a parent or educator to create and manage their
          account. Student seats are assigned by the adult who purchases them.
        </p>

        {previewRole && (
          <p style={s.preview}>
            You'll be set up for: <code>{previewRole}</code>
          </p>
        )}

        {/* Directly above the button that creates the account — proximity to
            the affirmative act is what makes clickwrap hold up. */}
        <TermsAcceptance
          checked={form.acceptedTerms}
          onChange={(v) => setForm((f) => ({ ...f, acceptedTerms: v }))}
          context="signup"
          showError={Boolean(error) && !form.acceptedTerms}
        />

        <button
          type="button"
          style={{ ...s.primary, opacity: busy ? 0.6 : 1 }}
          disabled={busy}
          onClick={handleSubmit}
        >
          {busy ? "Saving…" : completing ? "Save and continue" : "Create account"}
        </button>

        {!completing && (
          <p style={s.foot}>
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        )}
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: "100vh",
    background: "#f1f5f9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  card: {
    background: "#fff",
    maxWidth: 560,
    width: "100%",
    borderRadius: 12,
    padding: 32,
    boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
  },
  title: { margin: 0, fontSize: "clamp(1.5rem, 4vw, 2rem)", color: "#111" },
  sub: { color: "#555", fontSize: 14, marginTop: 6, marginBottom: 20, lineHeight: 1.5 },
  label: {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    marginTop: 14,
    marginBottom: 6,
  },
  hint: { fontSize: 11, color: "#6b7280", marginTop: 4, display: "block" },
  input: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid #d1d5db",
    fontSize: 15,
    boxSizing: "border-box",
  },
  row: { display: "flex", gap: 12, flexWrap: "wrap" },
  col: { flex: "1 1 200px", minWidth: 0 },
  signedInAs: { fontSize: 13, color: "#374151", marginTop: 14 },
  roleGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 10,
  },
  roleBtn: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    textAlign: "left",
    padding: "12px 14px",
    borderRadius: 10,
    border: "2px solid #e5e7eb",
    background: "#fff",
    cursor: "pointer",
    fontSize: 14,
  },
  roleBtnActive: {
    borderColor: "#7f00ff",
    background: "linear-gradient(90deg, rgba(225,0,255,0.06), rgba(127,0,255,0.06))",
  },
  roleHint: { fontSize: 12, color: "#6b7280", fontWeight: 400 },
  minorNote: { fontSize: 12, color: "#6b7280", marginTop: 14, lineHeight: 1.5 },

  scheduleBlock: {
    marginTop: 18,
    padding: 16,
    borderRadius: 14,
    background: "rgba(14, 165, 233, 0.05)",
    border: "1px solid rgba(14, 165, 233, 0.2)",
  },
  scheduleHelp: {
    fontSize: 13,
    lineHeight: 1.5,
    color: "#4a4458",
    margin: "4px 0 12px",
  },
  scheduleGroup: { marginTop: 12 },
  scheduleSubject: {
    fontSize: 13,
    fontWeight: 800,
    color: "#0c4a6e",
    marginBottom: 5,
  },
  scheduleRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    fontSize: 14,
    color: "#374151",
    lineHeight: 1.45,
    padding: "3px 0",
    cursor: "pointer",
  },
  // Sized explicitly. A bare `.form-wrapper input` rule has already stretched
  // a checkbox to full width once in this app — see the login popup. Radios
  // in a new block are exactly where that would happen again.
  scheduleRadio: {
    width: 16,
    height: 16,
    minWidth: 16,
    marginTop: 3,
    accentColor: "#0ea5e9",
  },
  scheduleNone: { color: "#6b7280" },

  eduCodeBlock: {
    marginTop: 18,
    padding: 16,
    borderRadius: 14,
    background: "rgba(127, 0, 255, 0.05)",
    border: "1px solid rgba(127, 0, 255, 0.18)",
  },
  optional: { fontWeight: 400, color: "#6b7280", fontSize: 13 },
  namePreview: {
    margin: "-6px 0 14px",
    fontSize: 13,
    color: "#4a4458",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "8px 12px",
  },
  eduCodeHelp: {
    fontSize: 13,
    lineHeight: 1.5,
    color: "#4a4458",
    margin: "4px 0 10px",
  },
  eduCodeRow: { display: "flex", gap: 8, alignItems: "stretch" },
  eduCodeCheck: {
    flexShrink: 0,
    border: 0,
    borderRadius: 10,
    padding: "0 18px",
    fontSize: 14,
    fontWeight: 700,
    color: "#fff",
    cursor: "pointer",
    background: "linear-gradient(135deg, #e100ff, #7f00ff)",
  },
  eduCodeSearching: { fontSize: 13, color: "#6b7280", margin: "10px 0 0" },
  eduCodeBad: {
    fontSize: 13,
    lineHeight: 1.5,
    color: "#8c2f22",
    margin: "10px 0 0",
  },
  eduCodeGood: {
    marginTop: 10,
    padding: "10px 14px",
    borderRadius: 10,
    background: "rgba(47, 138, 63, 0.1)",
    border: "1px solid rgba(47, 138, 63, 0.3)",
    fontSize: 14,
    color: "#1f5c30",
  },
  eduCodeConfirm: { fontSize: 12.5, marginTop: 4, color: "#2f6b40" },
  preview: { fontSize: 13, color: "#374151", marginTop: 10 },
  primary: {
    width: "100%",
    marginTop: 20,
    padding: "12px 18px",
    borderRadius: 8,
    border: "none",
    background: "linear-gradient(90deg, #e100ff, #7f00ff)",
    color: "#fff",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
  },
  foot: { textAlign: "center", fontSize: 14, marginTop: 16, color: "#555" },
  error: {
    background: "#fee2e2",
    border: "1px solid #fca5a5",
    color: "#991b1b",
    padding: "10px 12px",
    borderRadius: 8,
    fontSize: 14,
    marginBottom: 8,
  },
};
